// docs for router https://github.com/thepassle/app-tools/blob/master/router/README.md

import { html } from 'lit';

if (!(globalThis as any).URLPattern) {
  await import("urlpattern-polyfill");
}

import { Router } from '@thepassle/app-tools/router.js';

import './pages/app-home.js';
// Eager: the front page, the top-nav pages, and the small auth/profile screens
// that have to paint on a direct load or refresh. Every heavy page below is
// lazy() instead, and no page is ever both.
import './pages/app-store.js';
import './pages/app-login.js';
import './pages/app-register.js';
import './pages/app-forgot-password.js';
import './pages/app-reset-password.js';
import './pages/app-profile.js';
import './pages/app-two-factor.js';
import './components/notification-bell.js';

const baseURL: string = (import.meta as any).env.BASE_URL;

// The upstream lazy plugin calls the dynamic import but does not return the
// promise, so the router's `await beforeNavigation()` resolves instantly and
// renders an unupgraded, empty page tag until the chunk lands. Returning the
// promise makes it actually wait; the catch turns a chunk that no longer exists
// (stale tab after a redeploy) into one reload instead of a blank page forever.
const lazy = (fn: () => Promise<unknown>) => ({
  name: 'lazy',
  beforeNavigation: () => fn().then(
    () => { try { sessionStorage.removeItem('tk_chunk_retry'); } catch {} },
    (err: unknown) => {
      console.error('[router] chunk failed to load', err);
      try {
        if (!sessionStorage.getItem('tk_chunk_retry')) {
          sessionStorage.setItem('tk_chunk_retry', '1');
          location.reload();
        }
      } catch {}
    },
  ),
});


export const router = new Router({
    routes: [
      {
        path: resolveRouterPath(),
        title: 'Home',
        render: () => html`<app-home></app-home>`
      },
      {
        path: resolveRouterPath('settings'),
        title: 'Settings',
        render: () => { window.location.replace('/account'); return html``; }
      },
      {
        path: resolveRouterPath('account'),
        title: 'Account Center',
        plugins: [
          lazy(() => import('./pages/app-account-center.js')),
        ],
        render: () => html`<app-account-center></app-account-center>`
      },
      {
        path: resolveRouterPath('db-explorer'),
        title: 'DB Explorer',
        plugins: [
          lazy(() => import('./pages/app-db-explorer.js')),
        ],
        render: () => html`<app-db-explorer></app-db-explorer>`
      },
      {
        path: resolveRouterPath('billing'),
        title: 'Manage Billing',
        plugins: [
          lazy(() => import('./pages/app-billing.js')),
        ],
        render: () => html`<app-billing></app-billing>`
      },
      {
        path: resolveRouterPath('login'),
        title: 'Login',
        render: () => html`<app-login></app-login>`
      },
      {
        path: resolveRouterPath('register'),
        title: 'Create Account',
        render: () => html`<app-register></app-register>`
      },
      {
        path: resolveRouterPath('forgot-password'),
        title: 'Forgot Password',
        render: () => html`<app-forgot-password></app-forgot-password>`
      },
      {
        path: resolveRouterPath('reset-password'),
        title: 'Reset Password',
        render: () => html`<app-reset-password></app-reset-password>`
      },
      {
        path: resolveRouterPath('u/:username'),
        title: 'Profile',
        render: ({ params }: any) => html`<app-profile .username=${params?.username || ''}></app-profile>`
      },
      {
        path: resolveRouterPath('2fa'),
        title: 'Two-Factor Auth',
        render: () => html`<app-two-factor></app-two-factor>`
      },
      {
        path: resolveRouterPath('cloud'),
        title: 'Teknik Cloud',
        plugins: [
          lazy(() => import('./pages/app-cloud.js')),
        ],
        render: () => html`<app-cloud></app-cloud>`
      },
      {
        path: resolveRouterPath('share/:token'),
        title: 'Shared File',
        plugins: [
          lazy(() => import('./pages/app-share.js')),
        ],
        render: ({ params }: any) => html`<app-share .token=${params?.token || ''}></app-share>`
      },
      {
        // Unlinked, and lazy on purpose: six cards have ever been made, so its
        // 764 lines should not ship in the main bundle to every visitor.
        path: resolveRouterPath('bday'),
        title: 'Birthday Card',
        plugins: [ lazy(() => import('./pages/app-bday.js')) ],
        render: () => html`<app-bday></app-bday>`
      },
      {
        path: resolveRouterPath('bday/:id'),
        title: 'Sign Birthday Card',
        plugins: [ lazy(() => import('./pages/app-bday.js')) ],
        render: ({ params }: any) => html`<app-bday .cardId=${params?.id || ''}></app-bday>`
      },
      {
        path: resolveRouterPath('store'),
        title: 'Store',
        render: () => html`<app-store></app-store>`
      },
      {
        path: resolveRouterPath('messages'),
        title: 'Messages',
        plugins: [ lazy(() => import('./pages/app-messages.js')) ],
        render: () => html`<app-messages></app-messages>`
      },
      {
        path: resolveRouterPath('messages/:userId'),
        title: 'Messages',
        plugins: [ lazy(() => import('./pages/app-messages.js')) ],
        render: ({ params }: any) => html`<app-messages .preloadUserId=${params?.userId}></app-messages>`
      },
      {
        path: resolveRouterPath('games'),
        title: 'Games',
        plugins: [ lazy(() => import('./pages/app-games.js')) ],
        render: () => html`<app-games></app-games>`
      },
      {
        path: resolveRouterPath('invite/:code'),
        title: 'Join Server',
        plugins: [ lazy(() => import('./pages/app-messages.js')) ],
        render: ({ params }: any) => html`<app-messages .inviteCode=${params?.code}></app-messages>`
      },
      {
        path: resolveRouterPath('admin'),
        title: 'Admin Panel',
        plugins: [ lazy(() => import('./pages/app-admin.js')) ],
        render: () => html`<app-admin></app-admin>`
      },
      {
        path: resolveRouterPath('oauth/authorize'),
        title: 'Authorize App',
        plugins: [ lazy(() => import('./pages/app-oauth-consent.js')) ],
        render: () => html`<app-oauth-consent></app-oauth-consent>`
      },
      {
        path: resolveRouterPath('books'),
        title: 'Books',
        plugins: [ lazy(() => import('./pages/app-books.js')) ],
        render: () => html`<app-books></app-books>`
      },
      {
        path: resolveRouterPath('books/edit/:slug'),
        title: 'Edit Book',
        plugins: [ lazy(() => import('./pages/app-book-edit.js')) ],
        render: ({ params }: any) => html`<app-book-edit .slug=${params?.slug}></app-book-edit>`
      },
      {
        path: resolveRouterPath('books/edit/g/:guildId/:slug'),
        title: 'Edit Book',
        plugins: [ lazy(() => import('./pages/app-book-edit.js')) ],
        render: ({ params }: any) => html`<app-book-edit .slug=${params?.slug} .guildId=${params?.guildId}></app-book-edit>`
      },
      {
        path: resolveRouterPath('books/read/:slug'),
        title: 'Read Book',
        plugins: [ lazy(() => import('./pages/app-book-read.js')) ],
        render: ({ params }: any) => html`<app-book-read .slug=${params?.slug}></app-book-read>`
      },
      {
        path: resolveRouterPath('books/read/g/:guildId/:slug'),
        title: 'Read Book',
        plugins: [ lazy(() => import('./pages/app-book-read.js')) ],
        render: ({ params }: any) => html`<app-book-read .slug=${params?.slug} .guildId=${params?.guildId}></app-book-read>`
      },
    ]
});

// This function will resolve a path with whatever Base URL was passed to the vite build process.
// Use of this function throughout the starter is not required, but highly recommended, especially if you plan to use GitHub Pages to deploy.
// If no arg is passed to this function, it will return the base URL.

export function resolveRouterPath(unresolvedPath?: string) {
  var resolvedPath = baseURL;
  if(unresolvedPath) {
    resolvedPath = resolvedPath + unresolvedPath;
  }

  return resolvedPath;
}
